import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Col, Form, Input, InputNumber, message, Modal, Row, Select,
  Space, Switch, Table, Tag, Tooltip
} from 'antd';
import { LockOutlined, DeleteOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { brandApi, brandSupplierApi } from './itemConfigApi';
import { supplierApi } from '../suppliers/supplierApi';
import '../../../../styles/erpModal.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Brand modal (Stage 6B.8A-3D).
//
// Mirrors PolishTypeModal: three modes (add / edit / view) sharing a
// single compact 680px-wide centered modal. Brand picker dropdowns on
// the item-create popups (Glass / Hardware / Profile) feed off this
// list. Code is uppercased + locked on system rows.
// ─────────────────────────────────────────────────────────────────

const initialForm = () => ({
  brandCode:    '',
  brandName:    '',
  displayName:  '',
  websiteUrl:   '',
  countryCode:  '',
  description:  '',
  sortOrder:    null,
  isActive:     true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function SectionTitle({ children }) {
  return (
    <div style={{
      margin: '0 0 4px', borderBottom: '1px solid #eef0f3', paddingBottom: 2,
      fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: '#64748b'
    }}>{children}</div>
  );
}

function InlineHint({ icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11, color: '#64748b', lineHeight: 1.4,
      margin: '-2px 0 6px'
    }}>
      {icon ? <span style={{ marginTop: 1 }}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  );
}

export default function BrandModal({ open, mode = 'add', record = null, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;
  const isSystem = (isEdit || isView) && record?.isSystem === true;
  const codeLocked = readOnly || isSystem;

  // Stage 6B.8A-3E — many-to-many with suppliers.
  // `links` mirrors the current attachment state; `suppliers` is the
  // lookup pool used by the "Add supplier" dropdown.
  const [links, setLinks]           = useState([]);          // [{supplierId, supplierCode, supplierName, isPrimary, supplierBrandCode, notes}]
  const [suppliers, setSuppliers]   = useState([]);          // pool for the add-dropdown
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierBusy, setSupplierBusy]     = useState(false);

  const loadSuppliers = useCallback(async () => {
    setSupplierBusy(true);
    try {
      const result = await supplierApi.list({ pageSize: 500 });
      const rows = (result && Array.isArray(result.rows)) ? result.rows : [];
      setSuppliers(rows
        .filter((r) => (r.status || 'active') === 'active')
        .map((r) => ({
          value: r.id,
          label: r.supplierName,
          code:  r.supplierCode || ''
        })));
    } catch (_e) {
      setSuppliers([]);
    } finally {
      setSupplierBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
      setLinks([]);
    } else {
      form.setFieldsValue({
        brandCode:    record.brandCode || '',
        brandName:    record.brandName || '',
        displayName:  record.displayName || '',
        websiteUrl:   record.websiteUrl || '',
        countryCode:  record.countryCode || '',
        description:  record.description || '',
        sortOrder:    Number.isFinite(record.sortOrder) ? record.sortOrder : null,
        isActive:     record.isActive !== false
      });
      // Load existing supplier attachments for edit / view.
      if (record.id) {
        brandSupplierApi.list(record.id)
          .then((rows) => setLinks(rows))
          .catch(() => setLinks([]));
      } else {
        setLinks([]);
      }
    }
    // Pool of suppliers — used by the Add dropdown. Always fresh per open.
    loadSuppliers();
  }, [open, mode, record, form, loadSuppliers]);

  const onSubmit = useCallback(async () => {
    if (readOnly) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) { return; }
    // Local guard: at most one primary supplier per brand.
    const primaryCount = links.reduce((n, l) => n + (l.isPrimary ? 1 : 0), 0);
    if (primaryCount > 1) {
      message.error('Only one supplier per brand can be marked primary.');
      return;
    }
    setBusy(true);
    try {
      let brandId;
      if (isEdit && record?.id) {
        const payload = {
          brandName:   values.brandName,
          displayName: values.displayName || null,
          websiteUrl:  values.websiteUrl || null,
          countryCode: values.countryCode || null,
          description: values.description || null,
          sortOrder:   values.sortOrder,
          isActive:    values.isActive !== false
        };
        if (!isSystem) payload.brandCode = values.brandCode;
        const updated = await brandApi.update(record.id, payload);
        brandId = updated?.id || record.id;
        message.success('Brand updated.');
      } else {
        const created = await brandApi.create({
          brandCode:   String(values.brandCode || '').trim().toUpperCase(),
          brandName:   String(values.brandName || '').trim(),
          displayName: values.displayName || null,
          websiteUrl:  values.websiteUrl || null,
          countryCode: values.countryCode ? String(values.countryCode).trim().toUpperCase() : null,
          description: values.description || null,
          sortOrder:   values.sortOrder,
          isActive:    values.isActive !== false
        });
        brandId = created?.id;
        message.success('Brand created.');
      }
      // Persist the supplier links separately. The endpoint is
      // idempotent — server diffs against the existing set. We don't
      // fail the whole save if this PUT fails; the brand is already
      // saved and the user can retry from the modal.
      if (brandId) {
        try {
          await brandSupplierApi.replace(brandId,
            links.map((l) => ({
              supplierId:        l.supplierId,
              isPrimary:         l.isPrimary === true,
              supplierBrandCode: l.supplierBrandCode || null,
              notes:             l.notes || null
            }))
          );
        } catch (e) {
          message.warning(unwrapApiError(e,
            'Brand saved, but the supplier links could not be updated. Reopen to retry.'));
        }
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit ? 'Failed to update brand.' : 'Failed to create brand.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, readOnly, isSystem, record, onClose, onSaved, links]);

  // Helpers for the supplier link table inside the modal.
  const linkedSupplierIds = useMemo(
    () => new Set(links.map((l) => l.supplierId)),
    [links]
  );
  const supplierOptions = useMemo(
    () => suppliers.filter((s) => !linkedSupplierIds.has(s.value)),
    [suppliers, linkedSupplierIds]
  );

  const addSupplier = useCallback((supplierId) => {
    if (!supplierId) return;
    const sup = suppliers.find((s) => s.value === supplierId);
    if (!sup) return;
    if (linkedSupplierIds.has(supplierId)) return;
    setLinks((prev) => [
      ...prev,
      {
        supplierId,
        supplierCode: sup.code,
        supplierName: sup.label,
        isPrimary: prev.length === 0,    // first attached supplier becomes primary by default
        supplierBrandCode: '',
        notes: ''
      }
    ]);
    setSupplierSearch('');
  }, [suppliers, linkedSupplierIds]);

  const removeSupplier = useCallback((supplierId) => {
    setLinks((prev) => prev.filter((l) => l.supplierId !== supplierId));
  }, []);

  const togglePrimary = useCallback((supplierId) => {
    setLinks((prev) => prev.map((l) => ({
      ...l,
      isPrimary: l.supplierId === supplierId ? !l.isPrimary : false
    })));
  }, []);

  const updateLinkField = useCallback((supplierId, field, value) => {
    setLinks((prev) => prev.map((l) => (
      l.supplierId === supplierId ? { ...l, [field]: value } : l
    )));
  }, []);

  const titleNode = useMemo(() => {
    const label = mode === 'add' ? 'New Brand' : `Brand — ${record?.brandCode || ''}`;
    const chips = [];
    if (record?.isSystem) chips.push(<Tag key="sys" icon={<LockOutlined />} color="default" style={{ marginInlineEnd: 0 }}>System</Tag>);
    if (record) chips.push(
      <Tag key="active" color={record.isActive === false ? 'default' : 'success'} style={{ marginInlineEnd: 0 }}>
        {record.isActive === false ? 'Inactive' : 'Active'}
      </Tag>);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 28 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{label}</span>
        {chips.length ? <Space size={6} wrap>{chips}</Space> : null}
      </div>
    );
  }, [mode, record]);

  return (
    <Modal
      open={open}
      title={titleNode}
      onCancel={onClose}
      onOk={readOnly ? onClose : onSubmit}
      okText={readOnly ? 'Close' : isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={680}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-enterprise-modal"
    >
      {isSystem ? (
        <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
          Seeded system brand. Code is locked; you can still rename,
          edit the website, description, sort order, and active flag.
        </InlineHint>
      ) : null}

      <Form form={form} layout="vertical" size="small" requiredMark="optional"
        disabled={readOnly} className="item-config-compact-form erp-enterprise-form">
        <style>{`
          .item-config-compact-form .ant-form-item { margin-bottom: 6px; }
          .item-config-compact-form .ant-form-item-label { padding-bottom: 1px; }
          .item-config-compact-form .ant-form-item-label > label { font-size: 11.5px; color: #475569; height: 18px; font-weight: 500; }
          .item-config-compact-form .ant-form-item-label > label .ant-form-item-optional { font-size: 10.5px; color: #94a3b8; }
          .item-config-compact-form .ant-input,
          .item-config-compact-form .ant-input-number-input,
          .item-config-compact-form .ant-select-selector { font-size: 12.5px; }
          .item-config-compact-form .ant-input-affix-wrapper { padding-top: 1px; padding-bottom: 1px; }
          .item-config-compact-form .ant-form-item-explain { font-size: 11px; min-height: 14px; }
          .item-config-compact-form .ant-select-single .ant-select-selector { height: 26px; }
          .item-config-compact-form .ant-select-single .ant-select-selector .ant-select-selection-search-input,
          .item-config-compact-form .ant-select-single .ant-select-selector .ant-select-selection-item,
          .item-config-compact-form .ant-select-single .ant-select-selector .ant-select-selection-placeholder { line-height: 24px; }
          .item-config-compact-form .ant-table-small .ant-table-thead > tr > th,
          .item-config-compact-form .ant-table-small .ant-table-tbody > tr > td { padding: 4px 8px; font-size: 12px; }
        `}</style>

        <SectionTitle>Basic</SectionTitle>
        <Row gutter={[8, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Brand Code" name="brandCode"
              rules={[
                { required: true, message: 'Required' },
                { max: 40, message: 'Max 40 chars' },
                { pattern: /^[A-Za-z0-9_-]+$/, message: 'A-Z, 0-9, _, -' }
              ]}>
              <Input
                maxLength={40}
                disabled={codeLocked}
                placeholder="e.g. SAINT_GOBAIN"
                suffix={codeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  brandCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Brand Name" name="brandName"
              rules={[
                { required: true, message: 'Required' },
                { max: 150, message: 'Max 150 chars' }
              ]}>
              <Input maxLength={150} placeholder="e.g. Saint-Gobain" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Display Name" name="displayName"
              tooltip="Optional friendly name shown to operators. Falls back to brand name when blank."
              rules={[{ max: 150, message: 'Max 150 chars' }]}>
              <Input maxLength={150} placeholder="Operator-facing label (optional)" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Country" name="countryCode"
              tooltip="ISO 3166-1 alpha-2 country code (e.g. IN, JP, DE)."
              rules={[{ pattern: /^[A-Za-z]{2}$/, message: '2-letter code' }]}>
              <Input
                maxLength={2}
                placeholder="IN"
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => form.setFieldsValue({
                  countryCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Website" name="websiteUrl"
              rules={[{ max: 300, message: 'Max 300 chars' }]}>
              <Input maxLength={300} placeholder="https://example.com" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} maxLength={2000} placeholder="Optional notes about this brand" />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Control</SectionTitle>
        <Row gutter={[8, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber min={0} max={999999} precision={0}
                style={{ width: '100%' }} placeholder="e.g. 10" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Suppliers</SectionTitle>
        <div style={{ fontSize: 11, color: '#64748b', margin: '-2px 0 6px', lineHeight: 1.4 }}>
          Many-to-many. Mark one supplier as primary for purchase orders.
        </div>
        {!readOnly ? (
          <Row gutter={[8, 0]} style={{ marginBottom: 6 }}>
            <Col xs={24}>
              <Select
                placeholder={supplierBusy ? 'Loading suppliers…' : 'Add a supplier…'}
                value={null}
                searchValue={supplierSearch}
                onSearch={setSupplierSearch}
                onChange={addSupplier}
                showSearch
                allowClear
                disabled={readOnly || supplierBusy}
                loading={supplierBusy}
                style={{ width: '100%' }}
                options={supplierOptions}
                filterOption={(input, option) => {
                  const q = (input || '').toLowerCase();
                  return (
                    String(option.label || '').toLowerCase().includes(q) ||
                    String(option.code || '').toLowerCase().includes(q)
                  );
                }}
                optionRender={(opt) => (
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{opt.data.label}</span>
                    {opt.data.code
                      ? <code style={{ color: '#888', fontSize: 11 }}>{opt.data.code}</code>
                      : null}
                  </span>
                )}
                notFoundContent={supplierBusy ? 'Loading…' : 'No more suppliers'}
              />
            </Col>
          </Row>
        ) : null}

        <Table
          size="small"
          rowKey="supplierId"
          dataSource={links}
          pagination={false}
          locale={{ emptyText: 'No suppliers attached to this brand yet.' }}
          columns={[
            {
              title: '', key: 'primary', width: 40, align: 'center',
              render: (_v, row) => (
                <Tooltip title={row.isPrimary ? 'Primary supplier' : 'Mark as primary'}>
                  <Button
                    type="text"
                    size="small"
                    disabled={readOnly}
                    icon={row.isPrimary
                      ? <StarFilled style={{ color: '#faad14' }} />
                      : <StarOutlined style={{ color: '#9ca3af' }} />}
                    onClick={() => togglePrimary(row.supplierId)}
                  />
                </Tooltip>
              )
            },
            {
              title: 'Supplier', dataIndex: 'supplierName', key: 'supplier',
              render: (_v, row) => (
                <Space size={6}>
                  <span style={{ fontWeight: 500 }}>{row.supplierName}</span>
                  {row.supplierCode
                    ? <code style={{ color: '#888', fontSize: 11 }}>{row.supplierCode}</code>
                    : null}
                  {row.isPrimary
                    ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>Primary</Tag>
                    : null}
                </Space>
              )
            },
            {
              title: "Supplier's Brand Code",
              dataIndex: 'supplierBrandCode',
              key: 'supplierBrandCode',
              width: 200,
              render: (_v, row) => (
                <Input
                  size="small"
                  value={row.supplierBrandCode || ''}
                  placeholder="Their SKU prefix (optional)"
                  disabled={readOnly}
                  maxLength={80}
                  onChange={(e) => updateLinkField(row.supplierId, 'supplierBrandCode', e.target.value)}
                />
              )
            },
            {
              title: '', key: 'actions', width: 48, align: 'center',
              render: (_v, row) => readOnly ? null : (
                <Tooltip title="Detach supplier">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeSupplier(row.supplierId)}
                  />
                </Tooltip>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  );
}
